/**
 * Shared Prompt Builders
 *
 * Single source of truth for all prompt-building logic used by both
 * client-side (src/services/bulkGenerationService.js) and
 * server-side (server/services/bulkGenerationJobService.js).
 *
 * All functions are pure — no runtime-specific dependencies.
 */

// ============================================================================
// STORY ARC DEFINITIONS (used by carousel narrative builders)
// ============================================================================

export const STORY_ARC_DEFINITIONS = {
    problem_solution: {
        name: 'Problem -> Solution',
        cardRoleGuide: {
            hook: 'Show the PROBLEM vividly — the pain point or frustrating situation before the solution exists',
            build: 'Introduce the product/solution as the direct answer to the problem shown in the previous card',
            proof: 'Demonstrate the solution working — evidence, results, or features in action that prove it solves the problem',
            cta: 'Show the resolved state — the problem from card 1 is now solved, invite the viewer to take action',
            support: 'Additional evidence or benefits that deepen the narrative'
        }
    },
    before_after: {
        name: 'Before -> After Transformation',
        cardRoleGuide: {
            hook: 'Show the BEFORE state — the struggle, the undesirable situation, the "old way"',
            build: 'Show the transformation beginning — the product enters the picture, change starts',
            proof: 'Show the AFTER state emerging — clear, visible improvement happening',
            cta: 'Show the fully transformed outcome — aspirational result with a clear call to action',
            support: 'Additional transformation angles or proof points'
        }
    },
    feature_showcase: {
        name: 'Feature Showcase',
        cardRoleGuide: {
            hook: 'Tease the overall product value — what makes it worth paying attention to',
            build: 'Spotlight feature #1 with a specific use case or benefit',
            proof: 'Spotlight feature #2 with a different angle or complementary benefit',
            cta: 'Bring all features together — show the complete package with a call to action',
            support: 'Spotlight additional features or use cases'
        }
    },
    educational_steps: {
        name: 'Step-by-Step Education',
        cardRoleGuide: {
            hook: 'State the problem or question the audience wants answered',
            build: 'Step 1 of the process or technique — show it clearly',
            proof: 'Steps 2-3 continuing the process — build toward the result',
            cta: 'Show the finished result and invite the viewer to try it themselves',
            support: 'Additional steps or tips in the process'
        }
    },
    testimonial_journey: {
        name: 'Testimonial Journey',
        cardRoleGuide: {
            hook: 'Introduce the customer and their initial problem or skepticism',
            build: 'Show the customer discovering or trying the product for the first time',
            proof: 'Show the customer experiencing concrete results and outcomes',
            cta: 'Customer endorsement with call to action — "join them"',
            support: 'Additional testimonial proof points'
        }
    }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract brand colors as an array from the brand object.
 * Handles both `brand.visual` (new) and `brand.branding.colors` (legacy) structures.
 */
export function getBrandColors(brand) {
    if (brand?.visual) {
        const colors = [
            brand.visual.primaryColor,
            brand.visual.secondaryColor,
            brand.visual.accentColor
        ].filter(Boolean);
        if (colors.length > 0) return colors;
    }
    if (brand?.branding?.colors?.length > 0) {
        return [...brand.branding.colors];
    }
    return [];
}

/**
 * Build pillar context string from a brief.
 * Extracted because this logic is duplicated in both image and video prompt builders.
 */
export function buildPillarContext(brief) {
    const pillarName = brief.pillarName || '';
    const pillarDescription = brief.pillarDescription || '';
    const pillarGuidelines = brief.pillarGuidelines || {};

    if (!pillarName) return '';

    const pillarTone = pillarGuidelines.toneOfVoice?.length > 0 ? pillarGuidelines.toneOfVoice.join(', ') : '';
    const pillarMessages = pillarGuidelines.keyMessages?.length > 0 ? pillarGuidelines.keyMessages.join('; ') : '';

    let context = `CONTENT PILLAR: "${pillarName}"${pillarDescription ? ` - ${pillarDescription}` : ''}`;
    if (pillarTone) context += `\nPILLAR TONE OF VOICE: ${pillarTone}`;
    if (pillarMessages) context += `\nKEY MESSAGES TO REINFORCE: ${pillarMessages}`;

    return context;
}

/**
 * Find a product by name (string) or from a brief object.
 * Accepts either a product name string or a full brief object.
 * When given a brief, checks productId first for exact lookup.
 */
export function findProduct(nameOrBrief, products = []) {
    if (!products || products.length === 0) return null;

    let productId = null;
    let productName = null;

    if (typeof nameOrBrief === 'object' && nameOrBrief !== null) {
        const briefData = nameOrBrief.brief || nameOrBrief;
        productId = briefData.productId || nameOrBrief.productId;
        productName = briefData.product;
    } else {
        productName = nameOrBrief;
    }

    // Try productId first (exact lookup)
    if (productId) {
        const byId = products.find(p => p.id === productId);
        if (byId) return byId;
    }

    if (!productName) return null;

    // Exact name match
    const exact = products.find(p => p.name?.toLowerCase() === productName.toLowerCase());
    if (exact) return exact;

    // Partial name match
    const partial = products.find(p =>
        p.name?.toLowerCase().includes(productName.toLowerCase()) ||
        productName.toLowerCase().includes(p.name?.toLowerCase())
    );
    if (partial) return partial;

    // Fallback to first product
    console.warn(`[findProduct] No match for "${productName}" — falling back to first product "${products[0]?.name}"`);
    return products[0] || null;
}

/**
 * Find product image URL by product name from products list.
 * @deprecated Use findProduct() instead for access to full product data.
 */
export function findProductImageUrl(productName, products = []) {
    const product = findProduct(productName, products);
    return product?.imageUrl || null;
}

// ============================================================================
// PROMPT BUILDERS
// ============================================================================

/**
 * Build an image generation prompt from a brief.
 * @param {Object} brief - The content brief
 * @param {Object} brand - The brand data
 * @param {Object} product - Optional product object with visualNotes, description, features, productUrl
 * @param {Object} options - { includeLogo: boolean, pillarContext: string }
 */
export function buildImagePrompt(brief, brand, product = null, { includeLogo = true, pillarContext = '' } = {}) {
    const briefData = brief.brief || brief;

    // Extract brand colors
    const brandColors = getBrandColors(brand);
    const colorString = brandColors.length > 0
        ? `BRAND COLOR PALETTE: ${brandColors.join(', ')}. IMPORTANT: Subtly weave these brand colors into the background, environment, lighting tones, and props. The background and setting should clearly reflect the brand palette — use tinted backgrounds, colored lighting gels, complementary surfaces, or environmental elements in these exact colors. The overall image should feel "on brand" at a glance.`
        : '';

    // Extract brand voice and messaging
    const brandVoice = brand?.voice?.tone || brand?.voice?.personality || brand?.branding?.brandVoice || '';
    const valueProposition = brand?.valueProposition || brand?.products || '';
    const industry = brand?.industry || '';

    // Extract brand fonts
    const headingFont = brand?.visual?.fonts?.heading || 'bold sans-serif';
    const bodyFont = brand?.visual?.fonts?.body || 'clean sans-serif';
    const primaryColor = brand?.visual?.primaryColor || '';
    const accentColor = brand?.visual?.accentColor || '';

    // Build pillar context if not provided externally
    let resolvedPillarContext = pillarContext;
    if (!resolvedPillarContext) {
        resolvedPillarContext = buildPillarContext(brief);
    }

    // Build product context with visual notes
    let productContext = '';
    if (briefData.product) {
        productContext = `Featured product/service: ${briefData.product}`;

        // Add visual notes for accurate product representation
        if (product?.visualNotes) {
            productContext += `\n\nPRODUCT VISUAL APPEARANCE (use this for accurate representation):\n${product.visualNotes}`;
        }

        // Add description for context
        if (product?.description) {
            productContext += `\n\nProduct Description: ${product.description}`;
        }

        // Add features if available
        if (product?.features?.length > 0) {
            productContext += `\n\nKey Features: ${product.features.join(', ')}`;
        }

        // Anti-competitor instruction
        productContext += `\n\nCRITICAL - PRODUCT IDENTITY: This ad is EXCLUSIVELY for "${briefData.product}" by ${brand?.name || 'this brand'}. Do NOT depict, reference, or substitute any competitor products, rival brands, or generic/stock versions of this product. The product shown must be recognizably "${briefData.product}" — not a similar-looking alternative from another company. If a product reference image is provided, match THAT specific product exactly.`;
    }

    const parts = [
        // Chain-of-thought preamble: force the model to study reference images before generating
        `Before generating, carefully study all reference images provided. Note:
- The product's exact shape, colors, labels, text, textures, and packaging details
- The logo's exact colors, typography, and design elements
- Any distinctive features that must be preserved exactly

Now, using those observations, create a professional advertisement image for ${brand?.name || 'a brand'}.`,

        // Pillar context - guides the creative direction
        resolvedPillarContext,

        // Scene and visual direction
        briefData.sceneDescription && `Scene: ${briefData.sceneDescription}`,
        briefData.visualStyle && `Visual Style: ${briefData.visualStyle}`,
        briefData.imageType && `Type: ${briefData.imageType}`,

        // Brand context
        industry && `Industry: ${industry}`,
        valueProposition && `Value Proposition: ${valueProposition}`,
        brandVoice && `Brand Voice/Tone: ${brandVoice}`,

        // Color direction
        colorString,

        // Text overlays - explicit instructions for text rendered ON the image
        briefData.selectedHeadline && `TEXT OVERLAY - HEADLINE: Display this text prominently on the image: "${briefData.selectedHeadline}". Use ${headingFont} font style. ${primaryColor ? `Text color: use ${primaryColor} or white for contrast against the background.` : 'Use white or high-contrast color.'}`,
        briefData.cta && `TEXT OVERLAY - CTA: Display this call-to-action text on the image: "${briefData.cta}". Make it visually distinct as a button or bold text. Use ${headingFont} font style. ${accentColor ? `Color: ${accentColor} or accent color.` : ''}`,
        briefData.caption && `MARKETING NARRATIVE (do NOT render on image — use to understand the message and emotional tone): ${briefData.caption}`,

        // Typography direction (body text only — headline/CTA font specified in TEXT OVERLAY sections above)
        `TYPOGRAPHY: Use ${bodyFont} for any secondary or body text. All text on the image must be clearly legible with strong contrast.`,

        // Marketing direction
        briefData.marketingAngle && `Marketing angle: ${briefData.marketingAngle}`,
        briefData.targetAudience && `Target audience: ${briefData.targetAudience}`,
        briefData.desiredEmotion && `Evoke emotion: ${briefData.desiredEmotion}`,
        briefData.tone && `Tone: ${briefData.tone}`,

        // Product context (includes visual notes, URL, competitor avoidance)
        productContext,

        // Technical requirements
        `REQUIREMENTS:`,
        `- High quality, professional advertising photography`,
        `- Modern aesthetic with brand-aligned composition`,
        `- All text overlays must be clearly readable with proper contrast`,
        `- Suitable for social media advertising`,

        // High-fidelity reproduction requirements
        `HIGH-FIDELITY REPRODUCTION:`,
        `- The reference images provided are ground-truth assets. The product and logo in the generated image must be visually identical to their reference images. Do not reimagine, stylize, or abstract them.`,
        includeLogo ? `- LOGO INTEGRITY: The brand logo must appear exactly as provided in the reference image — same colors, same composition, same text, same design. The logo's original colors from the reference image take absolute precedence — never recolor the logo to white, black, or any other color regardless of the scene. Never alter, redraw, or simplify the logo. Place it visibly in the composition (typically bottom corner or center).` : `- Do NOT include any brand logo on this image.`,
        briefData.product ? `- PRODUCT ACCURACY: The product must match its reference images exactly — same shape, color, packaging, labels, and proportions. Integrate it naturally into the scene (lighting, shadows, reflections) but preserve its exact visual identity. Do not invent, stylize, or substitute the product.` : null,
    ];

    return parts.filter(Boolean).join('\n');
}

/**
 * Build a video generation prompt from a brief.
 * Supports keyFrame-based briefs and falls back for older briefs.
 * @param {Object} brief - The content brief
 * @param {Object} brand - The brand data
 * @param {Object} product - Optional product object
 */
export function buildVideoPrompt(brief, brand, product = null) {
    const briefData = brief.brief || brief;
    const keyFrames = briefData.keyFrames || [];

    // Extract brand colors
    const brandColors = getBrandColors(brand);
    const colorString = brandColors.length > 0
        ? `BRAND COLOR PALETTE: ${brandColors.join(', ')}. IMPORTANT: Subtly weave these brand colors into backgrounds, environments, lighting tones, and props throughout the video. Scenes should reflect the brand palette — use tinted backgrounds, colored lighting gels, complementary surfaces, or environmental elements in these exact colors.`
        : '';

    // Extract brand fonts
    const headingFont = brand?.visual?.fonts?.heading || 'bold sans-serif';
    const bodyFont = brand?.visual?.fonts?.body || 'clean sans-serif';
    const primaryColor = brand?.visual?.primaryColor || '';

    // Extract brand voice and messaging
    const brandVoice = brand?.voice?.tone || brand?.voice?.personality || brand?.branding?.brandVoice || '';
    const valueProposition = brand?.valueProposition || brand?.products || '';

    // Build pillar context
    const pillarContext = buildPillarContext(brief);

    // Build product context
    let productContext = '';
    if (briefData.product) {
        productContext = `Featured product/service: ${briefData.product}`;
        if (product?.productUrl) {
            productContext += `\nProduct URL: ${product.productUrl}`;
        }
        if (product?.visualNotes) {
            productContext += `\nProduct appearance: ${product.visualNotes}`;
        }
        if (product?.description) {
            productContext += `\nProduct description: ${product.description}`;
        }
    }

    // Build keyframe breakdown if available
    const keyFrameSection = keyFrames.length > 0
        ? '\nKEYFRAME BREAKDOWN (follow this timeline exactly):\n' + keyFrames.map(kf => {
            const lines = [`[${kf.timeCode}]`];
            if (kf.visual) lines.push(`  VISUAL: ${kf.visual}`);
            if (kf.textOverlay) lines.push(`  TEXT ON SCREEN: "${kf.textOverlay}" — use ${headingFont} font, ${primaryColor || 'white'} color`);
            if (kf.audio) lines.push(`  AUDIO: ${kf.audio}`);
            return lines.join('\n');
        }).join('\n\n')
        : '';

    // Use new video fields if available, fall back to legacy videoSettings
    const videoLength = briefData.videoLength || briefData.videoSettings?.videoLength || '6s';
    const videoStyle = briefData.videoStyle || briefData.videoSettings?.videoStyle || 'commercial';

    const parts = [
        // Chain-of-thought preamble: force the model to study reference images before generating
        `Before generating, carefully study all reference images provided. Note:
- The product's exact shape, colors, labels, text, textures, and packaging details
- The logo's exact colors, typography, and design elements
- Any distinctive features that must be preserved exactly

Now, using those observations, create a ${videoLength} ${videoStyle} video advertisement for ${brand?.name || 'a brand'}.`,

        // Pillar context
        pillarContext,

        // Video narrative and visual world
        briefData.videoNarrative && `VIDEO NARRATIVE: ${briefData.videoNarrative}`,
        briefData.sceneDescription && `VISUAL WORLD & SETTING: ${briefData.sceneDescription}`,
        briefData.hookDescription && `OPENING HOOK (first 2 seconds): ${briefData.hookDescription}`,
        briefData.visualStyle && `Visual Style: ${briefData.visualStyle}`,

        // Keyframe breakdown
        keyFrameSection,

        // Text overlay direction
        briefData.selectedHeadline && `HEADLINE TEXT: "${briefData.selectedHeadline}" — display prominently using ${headingFont} font`,
        briefData.cta && `CTA TEXT: "${briefData.cta}" — display in final frames using ${headingFont} font`,
        briefData.musicMood && `MUSIC MOOD: ${briefData.musicMood}`,
        briefData.ctaTiming && `CTA TIMING: ${briefData.ctaTiming}`,

        // Brand context
        brand?.industry && `Industry: ${brand.industry}`,
        valueProposition && `Value Proposition: ${valueProposition}`,
        brandVoice && `Brand Voice/Tone: ${brandVoice}`,
        colorString,

        // Marketing direction
        briefData.marketingAngle && `Marketing angle: ${briefData.marketingAngle}`,
        briefData.targetAudience && `Target audience: ${briefData.targetAudience}`,
        briefData.desiredEmotion && `Evoke emotion: ${briefData.desiredEmotion}`,
        briefData.tone && `Tone: ${briefData.tone}`,

        // Product context
        productContext,

        // Typography
        `TYPOGRAPHY: Use ${headingFont} for headline overlays and ${bodyFont} for body text. All text must be clearly legible.`,

        // Technical requirements
        `REQUIREMENTS:`,
        `- Professional video quality with smooth motion`,
        `- Hook must grab attention in the first 2 seconds`,
        `- All text overlays clearly readable with proper contrast`,
        `- Brand colors evident throughout`,
        `- ${videoLength} duration`,
        `- Suitable for social media advertising`,

        // High-fidelity reproduction requirements
        `HIGH-FIDELITY REPRODUCTION:`,
        `- The reference images provided are ground-truth assets. The product and logo in the video must be visually identical to their reference images. Do not reimagine, stylize, or abstract them.`,
        `- LOGO INTEGRITY: The brand logo must appear in the final frame exactly as provided in the reference image — same colors, same composition, same text, same design. The logo's original colors from the reference image take absolute precedence — never recolor the logo to white, black, or any other color regardless of the scene. Never alter, redraw, or simplify the logo.`,
        briefData.product ? `- PRODUCT ACCURACY: The product must match its reference images exactly — same shape, color, packaging, labels, and proportions. Integrate it naturally into scenes (lighting, shadows, reflections) but preserve its exact visual identity. Do not invent, stylize, or substitute the product.` : null,
    ];

    return parts.filter(Boolean).join('\n');
}

// ============================================================================
// CAROUSEL NARRATIVE HELPERS
// ============================================================================

/**
 * Build a narrative context block that describes the entire carousel story.
 * Prepended to every card's image prompt so the AI understands the full
 * story and can generate visually cohesive cards.
 */
export function buildCarouselNarrativeContext(briefData) {
    const storyArcType = briefData.storyArcType || 'problem_solution';
    const storyNarrative = briefData.storyNarrative || '';
    const cards = briefData.carouselCards || [];
    const overallCta = briefData.cta || '';

    const arcInfo = STORY_ARC_DEFINITIONS[storyArcType] || STORY_ARC_DEFINITIONS.problem_solution;

    const headlineSequence = cards.map((c, i) =>
        `Card ${i + 1} (${c.cardRole || 'card'}): "${c.headline || 'No headline'}"`
    ).join(' -> ');

    const captionSequence = cards.map((c, i) =>
        `Card ${i + 1}: ${c.caption || ''}`
    ).filter(line => !line.endsWith(': ')).join('\n');

    const contextBlock = `
CAROUSEL NARRATIVE CONTEXT — THIS IMAGE IS PART OF A MULTI-CARD STORY:
Story Arc: ${arcInfo.name}
Overall Story: ${storyNarrative}
Total Cards: ${cards.length}
${overallCta ? `Overall CTA: ${overallCta}` : ''}

HEADLINE PROGRESSION: ${headlineSequence}

CAPTION STORY (the narrative these cards tell together):
${captionSequence}

VISUAL COHERENCE RULES:
- All cards share the same color palette, lighting mood, and visual style
- Characters/subjects should be recognizable across cards (same person, same product)
- Environmental elements should feel like the same world/setting evolving across the story
- Typography style must be identical across all cards`;

    return { contextBlock, arcInfo };
}

/**
 * Build position-aware context for a specific card, including what the
 * previous and next cards show so this card can maintain visual continuity.
 */
export function buildCardPositionContext(cardIndex, cards, arcInfo) {
    const card = cards[cardIndex] || {};
    const totalCards = cards.length;
    const role = card.cardRole || (cardIndex === 0 ? 'hook' : cardIndex === totalCards - 1 ? 'cta' : 'build');
    const roleGuidance = (arcInfo.cardRoleGuide || {})[role] || (arcInfo.cardRoleGuide || {}).support || '';

    const prevCard = cardIndex > 0 ? cards[cardIndex - 1] : null;
    const nextCard = cardIndex < totalCards - 1 ? cards[cardIndex + 1] : null;

    let context = `\nTHIS IS CARD ${cardIndex + 1} OF ${totalCards}.`;
    context += `\nROLE: ${role.toUpperCase()} — ${roleGuidance}`;

    if (prevCard) {
        const prevSummary = prevCard.headline || (prevCard.caption || '').substring(0, 80) || '';
        context += `\nPREVIOUS CARD showed: "${prevSummary}" — this card must VISUALLY CONTINUE from that scene.`;
    }

    if (nextCard) {
        const nextSummary = nextCard.headline || (nextCard.caption || '').substring(0, 80) || '';
        context += `\nNEXT CARD will show: "${nextSummary}" — this card must SET UP that transition.`;
    }

    return context;
}

// ============================================================================
// REFERENCE IMAGE BUILDER
// ============================================================================

/**
 * Build the reference images array (logo + product images) for the generation API.
 *
 * All reference images are sent as HIGH-FIDELITY OBJECT references so that
 * Gemini 3 Pro treats them as ground-truth assets to reproduce exactly.
 * Budget: up to 6 high-fidelity object images (1 logo + up to 5 product images).
 *
 * @param {Object} brand - Brand data (with logoUrl/logo)
 * @param {Object} product - Product data (with imageUrl, images array, visualNotes)
 * @param {Object} brief - The content brief
 * @param {Object} options - { includeLogo: boolean }
 */
export function buildReferenceImages(brand, product, brief, { includeLogo = true } = {}) {
    const referenceImages = [];
    const briefData = brief.brief || brief;
    const productName = briefData.product || product?.name;

    // Brand logo as high-fidelity object reference (conditionally included)
    const logoUrl = brand?.logoUrl || brand?.logo || brand?.branding?.logoUrl;
    if (logoUrl && includeLogo) {
        referenceImages.push({
            url: logoUrl,
            instruction: `HIGH-FIDELITY OBJECT — BRAND LOGO: This is the exact brand logo for ${brand?.name || 'the brand'}. Reproduce this logo EXACTLY as provided — same colors, same proportions, same composition, same text, same design. The logo's original colors from this reference image take absolute precedence — never recolor it to white, black, monochrome, or any other color regardless of the scene or background. Do not redraw, simplify, or modify the logo in any way. Place it in a corner or bottom portion of the composition, clearly visible and professionally integrated. This is a pixel-perfect ground-truth reference.`
        });
    }

    // Product images as high-fidelity object references (max 5 to stay within 6-object budget)
    let productImages = product?.images || [];

    // Ensure imageUrl is included
    if (product?.imageUrl && productImages.length === 0) {
        productImages = [{ url: product.imageUrl, alt: 'Primary product image' }];
    } else if (product?.imageUrl && !productImages.some(img => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        return imgUrl === product.imageUrl;
    })) {
        productImages = [{ url: product.imageUrl, alt: 'Primary product image' }, ...productImages];
    }

    // Cap at 5 product images (1 logo + 5 products = 6 high-fidelity object slots)
    const maxProductImages = includeLogo ? 5 : 6;
    productImages.slice(0, maxProductImages).forEach((img, index) => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        if (!imgUrl) return;

        let productInstruction = index === 0
            ? `HIGH-FIDELITY OBJECT — PRIMARY PRODUCT: This is the exact product "${productName || 'the product'}". Reproduce this product EXACTLY as shown — same shape, colors, labels, textures, packaging, and proportions. Integrate it naturally into the scene with correct lighting and shadows, but do NOT alter its appearance in any way. This is a ground-truth reference image — the product in the generated creative must be visually identical to this reference.`
            : `HIGH-FIDELITY OBJECT — PRODUCT VIEW ${index + 1}: Another angle/view of "${productName || 'the product'}". Use this additional reference to ensure the product is depicted with full accuracy from all perspectives. The product's shape, colors, labels, and textures must match these reference images exactly.`;

        // Add visual notes and description only to the first image
        if (index === 0) {
            if (product?.visualNotes) {
                productInstruction += `\n\nPRODUCT VISUAL DETAILS: ${product.visualNotes}`;
            }
            if (product?.description) {
                productInstruction += `\n\nPRODUCT DESCRIPTION: ${product.description}`;
            }
        }

        if (img.alt) {
            productInstruction += `\n\nImage context: ${img.alt}`;
        }

        referenceImages.push({
            url: imgUrl,
            instruction: productInstruction
        });
    });

    return referenceImages;
}

// ============================================================================
// PRODUCT PHOTO STUDIO BUILDERS
// ============================================================================

/**
 * Build a product photo studio prompt.
 * Focused on clean product photography — no marketing copy overlays
 * unless explicitly opted in via includeTextOverlay.
 *
 * @param {Object} product - Product data { name, description, visualNotes, features }
 * @param {Object} brand - Brand data with visual, voice, etc.
 * @param {Object} options - Scene and branding options
 */
export function buildProductPhotoPrompt(product, brand, options = {}) {
    const {
        scene = null,
        customPrompt = '',
        includeLogo = true,
        includeTextOverlay = false,
        headlineText = '',
        ctaText = '',
        brandColorIntensity = 'moderate',
        variantIndex = 0,
    } = options;

    const brandColors = getBrandColors(brand);

    const colorIntensityInstruction = {
        subtle: 'Use brand colors as very subtle accents only — a hint of color in lighting gels, a small prop in brand color, or a gentle background tint. The brand colors should be noticeable but not dominant.',
        moderate: 'Weave brand colors into the background, lighting, and props so the image clearly feels on-brand. Use tinted backgrounds, colored surfaces, or complementary objects in brand palette colors.',
        strong: 'Make brand colors prominent and dominant throughout the scene. Bold brand-colored backgrounds, prominent brand-palette props and surfaces. The brand identity should be immediately unmistakable.',
    };

    const colorString = brandColors.length > 0
        ? `BRAND COLOR PALETTE: ${brandColors.join(', ')}. ${colorIntensityInstruction[brandColorIntensity] || colorIntensityInstruction.moderate}`
        : '';

    const headingFont = brand?.visual?.fonts?.heading || 'bold sans-serif';
    const primaryColor = brand?.visual?.primaryColor || '';
    const accentColor = brand?.visual?.accentColor || '';

    // Build product context
    let productContext = '';
    if (product) {
        productContext = `PRODUCT: "${product.name || 'Product'}"`;
        if (product.visualNotes) {
            productContext += `\n\nPRODUCT VISUAL APPEARANCE (reproduce exactly):\n${product.visualNotes}`;
        }
        if (product.description) {
            productContext += `\n\nProduct Description: ${product.description}`;
        }
        if (product.features?.length > 0) {
            productContext += `\nKey Features: ${product.features.join(', ')}`;
        }
        productContext += `\n\nCRITICAL — PRODUCT IDENTITY: This photo is EXCLUSIVELY of "${product.name}" by ${brand?.name || 'this brand'}. Do NOT depict any competitor products or generic alternatives. If a product reference image is provided, match THAT specific product exactly.`;
    }

    // Scene instruction
    const sceneInstruction = scene?.promptTemplate || customPrompt || 'Professional product photography with clean, commercial-quality lighting and composition.';
    const additionalPrompt = scene && customPrompt ? `\n\nADDITIONAL DIRECTION: ${customPrompt}` : '';

    const parts = [
        `Before generating, carefully study all reference images provided. Note:
- The product's exact shape, colors, labels, text, textures, and packaging details
${includeLogo ? "- The logo's exact colors, typography, and design elements" : ''}
- Any distinctive features that must be preserved exactly

Now generate a professional product photo for ${brand?.name || 'a brand'}.`,

        `SCENE: ${sceneInstruction}`,
        additionalPrompt,

        brand?.industry && `Industry: ${brand.industry}`,
        colorString,

        // Text overlays (only if opted in)
        includeTextOverlay && headlineText && `TEXT OVERLAY — HEADLINE: Display this text on the image: "${headlineText}". Use ${headingFont} font style. ${primaryColor ? `Text color: ${primaryColor} or white for contrast.` : 'Use white or high-contrast color.'}`,
        includeTextOverlay && ctaText && `TEXT OVERLAY — CTA: Display this call-to-action: "${ctaText}". Make it visually distinct. ${accentColor ? `Color: ${accentColor}.` : ''}`,
        !includeTextOverlay && 'NO TEXT OVERLAYS: Do NOT include any text, typography, headlines, or written content on the image. This is a pure product photo.',

        productContext,

        `REQUIREMENTS:
- Professional product photography quality (not illustration or graphic design)
- Sharp focus on the product with appropriate depth of field
- Natural-looking lighting and shadows
- The product must be the clear hero and focal point of the composition
- Commercial-grade image suitable for e-commerce and marketing use`,

        `HIGH-FIDELITY REPRODUCTION:
- The reference images are ground-truth assets. The product in the generated image must be visually identical to the reference images.
- PRODUCT ACCURACY: Match the product's exact shape, color, packaging, labels, and proportions from the reference images. Integrate naturally into the scene but preserve exact visual identity.`,
        includeLogo
            ? `- LOGO INTEGRITY: The brand logo must appear exactly as provided — same colors, same design. Place it subtly in the composition (corner or bottom). The logo's original colors take absolute precedence.`
            : '- Do NOT include any brand logo on this image.',

        variantIndex > 0 && `VARIATION: Create a distinct variation from the previous generation — try a different camera angle, lighting setup, prop arrangement, or composition while maintaining the same scene concept and product accuracy.`,
    ];

    return parts.filter(Boolean).join('\n');
}

/**
 * Build reference images for the Product Photo Studio.
 * Handles three product sources: existing brand product, scanned product, or uploaded images.
 *
 * @param {Object} brand - Brand data (with logoUrl/logo)
 * @param {Object} product - Product data (with imageUrl, images array), or null if using uploads
 * @param {Array} uploadedImages - Array of uploaded image data URLs (when using upload source)
 * @param {Object} options - { includeLogo: boolean }
 */
export function buildProductPhotoReferenceImages(brand, product, uploadedImages = [], { includeLogo = true } = {}) {
    // If using uploaded images directly (no product object)
    if (uploadedImages.length > 0 && !product) {
        const refs = [];

        const logoUrl = brand?.logoUrl || brand?.logo || brand?.branding?.logoUrl;
        if (logoUrl && includeLogo) {
            refs.push({
                url: logoUrl,
                instruction: `HIGH-FIDELITY OBJECT — BRAND LOGO: This is the exact brand logo for ${brand?.name || 'the brand'}. Reproduce this logo EXACTLY as provided — same colors, same proportions, same design. Never recolor it. Place in a corner or bottom, clearly visible.`
            });
        }

        uploadedImages.forEach((img, i) => {
            refs.push({
                url: typeof img === 'string' ? img : img.url,
                instruction: i === 0
                    ? 'HIGH-FIDELITY OBJECT — PRIMARY PRODUCT: This is the product to photograph. Reproduce it exactly as shown — same shape, colors, labels, textures, and proportions.'
                    : `HIGH-FIDELITY OBJECT — PRODUCT VIEW ${i + 1}: Another angle of the same product. Use for accurate multi-angle reproduction.`
            });
        });

        return refs;
    }

    // For existing/scanned products, delegate to the standard builder
    if (product) {
        return buildReferenceImages(brand, product, { product: product.name }, { includeLogo });
    }

    // No product and no uploads — just logo if available
    const refs = [];
    const logoUrl = brand?.logoUrl || brand?.logo || brand?.branding?.logoUrl;
    if (logoUrl && includeLogo) {
        refs.push({
            url: logoUrl,
            instruction: `HIGH-FIDELITY OBJECT — BRAND LOGO: This is the exact brand logo for ${brand?.name || 'the brand'}. Reproduce this logo EXACTLY as provided. Never recolor.`
        });
    }
    return refs;
}
